import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Button,
  Progress,
  Card,
  Col,
  Divider,
  Empty,
  Flex,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Spin,
  Tag,
  Typography,
  Tooltip,
} from 'antd';
import { CheckCircleOutlined, CopyOutlined, ReloadOutlined, SendOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

import CodeMirrorEditor from "components/Editors/CodeMirrorEditor";
import { RenderedWhatsAppPreview } from "components/WhatsApp/WhatsAppLayout";
import { useNotification } from "NotificationContext";
import * as api from "api/activityTemplates";
import { copyText } from "utils/clipboard";

const { Text, Paragraph } = Typography;

/** Whichever number a record carries - leads and accounts name it differently. */
export const contactNumber = (record) =>
  record?.wa_number || record?.phone_number || record?.phone || "";

/** The rendered HTML of an email as the text a person would paste. */
const htmlToText = (html) => {
  if (!html) return "";
  const element = document.createElement("div");
  element.innerHTML = html;
  return (element.textContent || element.innerText || "").trim();
};

/**
 * Pick a template for a product/category, preview it against a lead, tweak it
 * and send. Used inline in the Add Lead Activity form and inside
 * SendTemplateDrawer for bulk sends from the leads list.
 *
 * What gets edited here is the template *source*, placeholders and all — the
 * preview pane shows how it renders for one lead. That keeps a bulk send
 * personalised instead of mailing everyone the first lead's rendered copy.
 *
 * Not every message can go out through the CRM: the lead may have no WhatsApp
 * number, or the 24-hour window may be shut. So the rendered message can also
 * be copied and the activity marked as sent, which logs exactly what a real
 * send would log minus the delivery - the timeline then tells the whole story
 * even when the rep sent it from their own phone.
 *
 * Sending is exposed through the ref so a parent form's own submit button can
 * drive it: `ref.current.hasTemplate()`, `ref.current.send({...})` and
 * `ref.current.copyMessage()`.
 */
const TemplateComposer = forwardRef(
  (
    {
      channel = "EMAIL",
      leads = [],
      // "lead" or "account" - the API keys both endpoints off whichever it is.
      recordType = "lead",
      compact = false,
      showActions = false,
      onSent,
      onTemplateChange,
    },
    ref
  ) => {
    const isEmail = channel === "EMAIL";
    const notify = useNotification();

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [templates, setTemplates] = useState([]);

    const [productId, setProductId] = useState(null);
    const [categoryId, setCategoryId] = useState(null);
    const [templateId, setTemplateId] = useState(null);

    // Email: the editable source. WhatsApp bodies are Meta-approved, so only
    // the placeholder values are editable there.
    const [subject, setSubject] = useState("");
    const [htmlContent, setHtmlContent] = useState("");
    const [variableOverrides, setVariableOverrides] = useState({});

    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [sending, setSending] = useState(false);
    // A bulk send is one request per lead, so the batch needs to report where
    // it has got to and which recipients failed — a toast capped at three
    // names is useless when twenty of fifty bounced.
    const [progress, setProgress] = useState(null);   // { done, total }
    const [failures, setFailures] = useState([]);
    // A bulk send handed to the server: { id, name, count }.
    const [batch, setBatch] = useState(null);

    const primaryLead = leads[0] || null;
    const template = useMemo(
      () => templates.find((item) => item.id === templateId) || null,
      [templates, templateId]
    );

    const recipients = useMemo(
      () => leads.filter((lead) => (isEmail ? lead?.email : lead?.wa_number)),
      [leads, isEmail]
    );
    const skipped = leads.length - recipients.length;

    // Marking is only a record of something already sent, so a lead with just
    // an ordinary phone number counts - that is the number the rep messaged.
    const markable = useMemo(
      () => leads.filter((lead) => (isEmail ? lead?.email : contactNumber(lead))),
      [leads, isEmail]
    );

    // -- lookups --------------------------------------------------------

    useEffect(() => {
      api
        .loadProducts()
        .then((response) => setProducts(api.rowsOf(response)))
        .catch(() => setProducts([]));
    }, []);

    // Default to the lead's own product when it has exactly one.
    useEffect(() => {
      if (productId) return;
      const leadProducts = primaryLead?.products || [];
      if (leadProducts.length === 1) {
        setProductId(leadProducts[0]?.id || leadProducts[0]);
      }
    }, [primaryLead, productId]);

    useEffect(() => {
      if (!productId) {
        setCategories([]);
        return;
      }
      api
        .loadCategories({ product: productId, is_active: true })
        .then((response) => setCategories(api.rowsOf(response)))
        .catch(() => setCategories([]));
    }, [productId]);

    useEffect(() => {
      if (!productId) {
        setTemplates([]);
        return;
      }
      const params = { product: productId, is_active: true };
      if (categoryId) params.template_category = categoryId;
      const load = isEmail ? api.loadEmailTemplates : api.loadWhatsappTemplates;
      load(params)
        .then((response) =>
          setTemplates(
            // Only what WhatsApp will actually deliver: drafts, pending and
            // rejected Meta templates would just fail at send time.
            api.rowsOf(response).filter(
              (item) => isEmail || item.kind === "MANUAL" || item.approval_status === "APPROVED"
            )
          )
        )
        .catch(() => setTemplates([]));
    }, [productId, categoryId, isEmail]);

    // Switching channel invalidates the chosen template.
    useEffect(() => {
      setTemplateId(null);
      setSubject("");
      setHtmlContent("");
      setVariableOverrides({});
      setPreview(null);
    }, [channel]);

    // -- selection ------------------------------------------------------

    const resetComposition = () => {
      setProgress(null);
      setFailures([]);
      setBatch(null);
      setSubject("");
      setHtmlContent("");
      setVariableOverrides({});
      setPreview(null);
    };

    const handleProductChange = (value) => {
      setProductId(value);
      setCategoryId(null);
      setTemplateId(null);
      resetComposition();
    };

    const handleCategoryChange = (value) => {
      setCategoryId(value);
      setTemplateId(null);
      resetComposition();
    };

    const handleTemplateChange = (value) => {
      setTemplateId(value);
      const chosen = templates.find((item) => item.id === value);
      if (onTemplateChange) onTemplateChange(chosen || null);
      setVariableOverrides({});
      setPreview(null);
      if (chosen && isEmail) {
        setSubject(chosen.subject || "");
        setHtmlContent(chosen.html_content || "");
      }
      if (chosen && primaryLead) {
        runPreview(chosen, {}, chosen.subject, chosen.html_content);
      }
    };

    // -- preview --------------------------------------------------------

    const runPreview = async (chosen, overrides, subjectSource, htmlSource) => {
      if (!chosen || !primaryLead) return null;
      setPreviewing(true);
      try {
        const target = { [recordType]: primaryLead.id };
        const response = isEmail
          ? await api.previewEmailTemplate(chosen.id, {
              ...target,
              subject: subjectSource,
              html_content: htmlSource,
            })
          : await api.previewWhatsappTemplate(chosen.id, {
              ...target,
              variables: overrides,
            });
        setPreview(response.data);
        setPreviewing(false);
        return response.data;
      } catch (err) {
        const detail = err?.response?.data?.error || "Could not render this template.";
        notify("error", "Preview failed", detail);
        setPreview(null);
      }
      setPreviewing(false);
      return null;
    };

    const refreshPreview = () =>
      runPreview(template, variableOverrides, subject, htmlContent);

    const setVariable = (component, index, value) => {
      setVariableOverrides((current) => {
        const next = { ...current, [component]: { ...(current[component] || {}) } };
        if (value === "") {
          // Clearing a field hands the slot back to the template's mapping, so
          // it renders per lead again.
          delete next[component][index];
          if (!Object.keys(next[component]).length) delete next[component];
        } else {
          next[component][index] = value;
        }
        return next;
      });
    };

    // -- send -----------------------------------------------------------

    /**
     * Send to every reachable recipient, or - with `markAsSent` - only record
     * that the messages went out by hand. Both walk the same list one request
     * at a time, so each recipient's message is rendered with their own details.
     */
    const send = async ({ activityStatus, notes, markAsSent = false } = {}) => {
      if (!template) {
        notify("warning", "Pick a template", "Choose a template before sending.");
        return { sent: 0, failures: ["No template selected"] };
      }
      const targets = markAsSent ? markable : recipients;
      if (!targets.length) {
        notify(
          "warning",
          markAsSent ? "Nothing to record" : "Nobody to send to",
          isEmail
            ? "No selected lead has an email address."
            : markAsSent
              ? "No selected lead has a phone or WhatsApp number to log this against."
              : "No selected lead has a WhatsApp number."
        );
        return { sent: 0, failures: ["No reachable recipient"] };
      }

      // A real send from the bulk drawer goes to the server as one batch: it
      // renders and sends in the background (so the tab can be closed),
      // retries temporary failures, and keeps every failure on the batch page.
      if (!markAsSent && showActions) {
        return sendBatch(targets, { activityStatus, notes });
      }

      setSending(true);
      setFailures([]);
      setProgress({ done: 0, total: targets.length });
      let sent = 0;
      const failures = [];

      for (const lead of targets) {
        const payload = { [recordType]: lead.id, template: template.id };
        if (activityStatus) payload.activity_status = activityStatus;
        if (notes) payload.notes = notes;
        if (markAsSent) payload.mark_as_sent = true;
        try {
          if (isEmail) {
            await api.sendActivityEmail({
              ...payload,
              subject,
              html_content: htmlContent,
            });
          } else {
            await api.sendActivityWhatsapp({
              ...payload,
              variables: variableOverrides,
              // Says which number it went to when the record has no WhatsApp
              // number of its own.
              ...(markAsSent ? { wa_number: contactNumber(lead) } : {}),
            });
          }
          sent += 1;
        } catch (err) {
          const data = err?.response?.data;
          const detail =
            data?.error ||
            (data && typeof data === "object" ? Object.values(data).flat().join(" ") : "") ||
            "Unknown error";
          failures.push(`${lead.first_name || lead.id}: ${detail}`);
        }
        setProgress({ done: sent + failures.length, total: targets.length });
      }
      setSending(false);
      setFailures(failures);

      if (sent) {
        notify(
          "success",
          markAsSent
            ? `Marked ${sent} as sent`
            : `Queued ${sent} ${isEmail ? "email" : "message"}${sent > 1 ? "s" : ""}`,
          markAsSent
            ? "The activity is logged with the message that went out; nothing was sent from here."
            : "Delivery runs in the background; the lead activity is already logged."
        );
      }
      if (failures.length) {
        // The full list stays on screen; the toast is only the headline.
        notify(
          "error",
          `${failures.length} of ${targets.length} failed`,
          "See the details on the send panel."
        );
      }
      if (sent && onSent) onSent();
      return { sent, failures };
    };

    const sendBatch = async (targets, { activityStatus, notes }) => {
      setSending(true);
      setFailures([]);
      setProgress(null);
      setBatch(null);
      const payload = {
        channel,
        record_type: recordType,
        record_ids: targets.map((lead) => lead.id),
        template: template.id,
        ...(activityStatus ? { activity_status: activityStatus } : {}),
        ...(notes ? { notes } : {}),
        ...(isEmail
          ? { subject, html_content: htmlContent }
          : { variables: variableOverrides }),
      };
      try {
        const { data } = await api.createBatch(payload);
        setBatch({ id: data.id, name: data.name, count: targets.length });
        notify(
          "success",
          `Sending to ${targets.length} in the background`,
          "You can close this panel. Track progress and retry failures under Send Batches."
        );
        if (onSent) onSent();
        return { sent: targets.length, failures: [], batch: data };
      } catch (err) {
        const data = err?.response?.data;
        const detail =
          data?.error ||
          (data && typeof data === "object" ? Object.values(data).flat().join(" ") : "") ||
          "Unknown error";
        setFailures([`Could not start the batch: ${detail}`]);
        notify("error", "Could not start the send", detail);
        return { sent: 0, failures: [detail] };
      } finally {
        setSending(false);
      }
    };

    /** The rendered message as pasteable text, for sending it by hand. */
    const messageText = (rendered) => {
      if (!rendered) return "";
      if (isEmail) {
        return [rendered.subject, htmlToText(rendered.html_content)]
          .filter(Boolean)
          .join("\n\n");
      }
      return [rendered.header, rendered.body, rendered.footer].filter(Boolean).join("\n\n");
    };

    const copyMessage = async () => {
      if (!template) {
        notify("warning", "Pick a template", "Choose a template before copying.");
        return false;
      }
      // The preview is what gets copied, so render it first if it is not up.
      const rendered =
        preview || (await runPreview(template, variableOverrides, subject, htmlContent));
      const text = messageText(rendered);
      if (!text) {
        notify("error", "Nothing to copy", "The message could not be rendered.");
        return false;
      }
      const copied = await copyText(text);
      notify(
        copied ? "success" : "error",
        copied ? "Message copied" : "Could not copy the message",
        copied
          ? "Paste it into WhatsApp, then mark the activity as sent."
          : "Select the preview text and copy it by hand."
      );
      return copied;
    };

    useImperativeHandle(ref, () => ({
      hasTemplate: () => !!template,
      isSending: () => sending,
      send,
      copyMessage,
      markAsSent: (options) => send({ ...options, markAsSent: true }),
      canMark: () => markable.length > 0,
    }));

    // -- render ---------------------------------------------------------

    const placeholderRows = (component) => {
      const indexes = template?.placeholders?.[component] || [];
      if (!indexes.length) return null;
      return (
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ textTransform: "capitalize" }}>
            {component} variables
          </Text>
          {indexes.map((index) => (
            <Input
              key={`${component}-${index}`}
              style={{ marginTop: 6 }}
              addonBefore={`{{${index}}}`}
              placeholder={
                preview?.variables?.[component]?.[index] || "From the template's mapping"
              }
              value={variableOverrides?.[component]?.[index] ?? ""}
              onChange={(event) => setVariable(component, index, event.target.value)}
            />
          ))}
        </div>
      );
    };

    const picker = (
      <>
        <Form layout="vertical" component={false}>
          <Form.Item label="Product" required style={{ marginBottom: 12 }}>
            <Select
              placeholder="Select a product"
              value={productId}
              onChange={handleProductChange}
              options={products.map((product) => ({
                label: product.name,
                value: product.id,
              }))}
            />
          </Form.Item>
          <Form.Item label="Category" style={{ marginBottom: 12 }}>
            <Select
              placeholder={productId ? "All categories" : "Pick a product first"}
              value={categoryId}
              onChange={handleCategoryChange}
              disabled={!productId}
              allowClear
              options={categories.map((category) => ({
                label: category.name,
                value: category.id,
              }))}
            />
          </Form.Item>
          <Form.Item label="Template" required style={{ marginBottom: 12 }}>
            <Select
              placeholder={productId ? "Select a template" : "Pick a product first"}
              value={templateId}
              onChange={handleTemplateChange}
              disabled={!productId}
              notFoundContent="No templates for this product"
              options={templates.map((item) => ({
                // Manual WhatsApp templates have no Meta language or status.
                label:
                  isEmail || !item.language
                    ? item.title
                    : `${item.title} (${item.language}${
                        item.approval_status ? ` · ${item.approval_status}` : ""
                      })`,
                value: item.id,
              }))}
            />
          </Form.Item>
        </Form>

        {template && isEmail && (
          <>
            <Form layout="vertical" component={false}>
              <Form.Item
                label="Subject"
                help="Placeholders like {{ first_name }} are filled in per lead."
                style={{ marginBottom: 12 }}
              >
                <Input value={subject} onChange={(event) => setSubject(event.target.value)} />
              </Form.Item>
            </Form>
            <Text strong>Body (HTML)</Text>
            <div style={{ marginTop: 6 }}>
              {/* Edited as source: a rich-text editor rewrites the template's
                  tables and inline styles into its own markup, so the email
                  went out as plain rich text. Uncontrolled — remount it when
                  the template changes. */}
              <CodeMirrorEditor
                key={templateId}
                initialValue={htmlContent}
                setCodeMirrorData={setHtmlContent}
              />
            </div>
          </>
        )}

        {template && !isEmail && (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                template.is_manual
                  ? "Manual template: sent as written, with {{ placeholders }} filled per lead. WhatsApp only delivers it inside the 24-hour reply window."
                  : "The message body is approved by Meta and cannot be edited here — only the variable values."
              }
            />
            {placeholderRows("header")}
            {placeholderRows("body")}
            {!template?.placeholders?.header?.length &&
              !template?.placeholders?.body?.length && (
                <Text type="secondary">This template has no variables.</Text>
              )}
          </>
        )}
      </>
    );

    const previewPane = (
      <>
        {skipped > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${skipped} selected lead${skipped > 1 ? "s have" : " has"} no ${
              isEmail ? "email address" : "WhatsApp number"
            } and will be skipped when sending.`}
            description={
              !isEmail && markable.length > recipients.length
                ? "Copy the message, send it from your own phone and use Mark as sent to log it against the number on the record."
                : undefined
            }
          />
        )}

        <Spin spinning={previewing}>
          {!template && <Empty description="Select a template to preview it" />}

          {template && isEmail && preview && (
            <>
              <Paragraph style={{ marginBottom: 4 }}>
                <Text type="secondary">To: </Text>
                <Text>{preview.to_email || primaryLead?.email}</Text>
              </Paragraph>
              <Paragraph style={{ marginBottom: 8 }}>
                <Text type="secondary">Subject: </Text>
                <Text strong>{preview.subject}</Text>
              </Paragraph>
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                  padding: 12,
                  background: "#fff",
                }}
                dangerouslySetInnerHTML={{ __html: preview.html_content }}
              />
            </>
          )}

          {template && !isEmail && preview && (
            <>
              <Paragraph style={{ marginBottom: 8 }}>
                <Text type="secondary">To: </Text>
                <Text>{preview.to_number || primaryLead?.wa_number}</Text>
              </Paragraph>
              <RenderedWhatsAppPreview rendered={preview} title={primaryLead ? "As this lead will see it" : "Preview"} />
            </>
          )}

          {template && !preview && !previewing && (
            <Flex justify="center" style={{ padding: 24 }}>
              <Button onClick={refreshPreview}>Render preview</Button>
            </Flex>
          )}
        </Spin>
      </>
    );

    const batchReport = (progress || batch || failures.length > 0) && (
      <div style={{ marginTop: 12 }}>
        {batch && (
          <Alert
            type="success"
            showIcon
            message={`Sending to ${batch.count} in the background`}
            description={
              <>
                Messages go out at the mailbox's pace (daily limit and delay), and temporary
                failures are retried automatically. You can close this panel.{" "}
                <Link to={`/activity-templates/batches?batch=${batch.id}`}>
                  View progress and failures
                </Link>
              </>
            }
          />
        )}
        {progress && (
          <Progress
            percent={Math.round((progress.done / Math.max(progress.total, 1)) * 100)}
            status={sending ? "active" : failures.length ? "exception" : "success"}
            format={() => `${progress.done} / ${progress.total}`}
          />
        )}
        {failures.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 8 }}
            message={`${failures.length} could not be sent`}
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {failures.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            }
          />
        )}
      </div>
    );

    const actions = showActions && (
      <Flex justify="end" gap="small" wrap="wrap" style={{ marginTop: 16 }}>
        <Tooltip title="Refresh preview">
          <Button
            icon={<ReloadOutlined />}
            onClick={refreshPreview}
            disabled={!template}
            aria-label="Refresh preview"
          />
        </Tooltip>
        <Tooltip
          title={
            isEmail
              ? "Copy the rendered subject and body"
              : "Copy the message so you can send it from your own phone"
          }
        >
          <Button icon={<CopyOutlined />} onClick={copyMessage} disabled={!template}>
            Copy message
          </Button>
        </Tooltip>
        <Popconfirm
          title="Mark as sent"
          description={`Log this ${
            isEmail ? "email" : "message"
          } as already sent to ${markable.length} ${
            markable.length === 1 ? "recipient" : "recipients"
          }? Nothing will be sent from here.`}
          okText="Mark as sent"
          onConfirm={() => send({ markAsSent: true })}
          disabled={!template || !markable.length}
        >
          <Button
            icon={<CheckCircleOutlined />}
            loading={sending}
            disabled={!template || !markable.length}
          >
            Mark as sent
          </Button>
        </Popconfirm>
        <Button
          type="primary"
          icon={<SendOutlined />}
          loading={sending}
          onClick={() => send()}
          // Already handed over: a second click would send the batch twice.
          disabled={!template || !recipients.length || !!batch}
        >
          {sending
            ? progress
              ? `Sending ${progress.done}/${progress.total}`
              : "Starting..."
            : `Send to ${recipients.length}`}
        </Button>
      </Flex>
    );

    // Narrow container (the activity drawer): stack picker over preview.
    if (compact) {
      return (
        <div>
          {picker}
          {batchReport}
          {template && (
            <>
              <Divider style={{ margin: "16px 0 12px" }}>
                <Flex gap="small" align="center">
                  <Text type="secondary">
                    Preview
                    {primaryLead
                      ? ` for ${`${primaryLead.first_name || ""} ${
                          primaryLead.last_name || ""
                        }`.trim()}`
                      : ""}
                  </Text>
                  <Tooltip title="Refresh preview">
                    <Button
                      size="small"
                      type="text"
                      icon={<ReloadOutlined />}
                      onClick={refreshPreview}
                    />
                  </Tooltip>
                  <Tooltip title="Copy this message">
                    <Button
                      size="small"
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={copyMessage}
                    />
                  </Tooltip>
                </Flex>
              </Divider>
              {previewPane}
            </>
          )}
          {actions}
        </div>
      );
    }

    return (
      <>
        <Row gutter={16}>
          <Col span={11}>
            <Card size="small" title="Template" bordered={false}>
              {picker}
            </Card>
          </Col>
          <Col span={13}>
            <Card
              size="small"
              bordered={false}
              title={
                primaryLead
                  ? `Preview for ${`${primaryLead.first_name || ""} ${
                      primaryLead.last_name || ""
                    }`.trim()}`
                  : "Preview"
              }
              extra={
                <Flex gap="small" align="center">
                  {leads.length > 1 && (
                    <Tag color="blue">{leads.length} leads selected</Tag>
                  )}
                  <Tooltip title="Copy this message">
                    <Button
                      size="small"
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={copyMessage}
                      disabled={!template}
                    />
                  </Tooltip>
                </Flex>
              }
            >
              {previewPane}
            </Card>
          </Col>
        </Row>
        {batchReport}
        {actions}
      </>
    );
  }
);

export default TemplateComposer;
